import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { ASSETS } from "../lib/assets";
import { useLanguage } from "@/i18n/LanguageProvider";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const NAV = [
  { to: "/", key: "home" },
  { to: "/services", key: "services" },
  { to: "/staffing", key: "staffing" },
  { to: "/experiences", key: "portfolio" },
  { to: "/about", key: "about" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 w-full z-50 transition-colors duration-500 ${
          scrolled ? "bg-evo-black/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img src={ASSETS.logo} alt="EVO Global Exhibitions" className="evo-logo h-11 md:h-12 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]" />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `link-underline text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "text-evo-gold" : "text-evo-pearl/80 hover:text-evo-pearl"
                  }`
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            <Link
              to="/contact"
              data-testid="header-quote-btn"
              className="hidden md:inline-flex items-center gap-2 border border-evo-gold/60 text-evo-gold hover:bg-evo-gold hover:text-evo-black transition-colors duration-300 px-6 py-3 text-xs uppercase tracking-[0.2em] group"
            >
              {t("common.requestQuote")}
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="menu-open-btn"
              className="md:hidden text-evo-pearl p-2"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-evo-black md:hidden flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-white/5">
              <img src={ASSETS.logo} alt="EVO" className="evo-logo h-10 w-auto object-contain" />
              <button onClick={() => setOpen(false)} data-testid="menu-close-btn" className="text-evo-pearl p-2" aria-label="Close menu">
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {[...NAV, { to: "/contact", key: "contact" }].map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.key}`}
                    className="font-serif text-5xl text-evo-pearl hover:text-evo-gold transition-colors block py-3"
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-12">
              <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-4">Language</p>
              <LanguageSwitcher variant="mobile" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
